<template lang="pug">
SideOverlaySlot(
  :isOpen="isOpen",
  :title="overlayTitle",
  @closeOverlay="closeOverlay"
)
  template(#contentTop)
    form#manage-user(
      @submit.prevent="saveAndCloseOverlay",
      novalidate
    )
      .form-group(:class="{ 'form-group__invalid': v$.showUser.idUserRole.$errors.length }")
        label(for="user-type") User Type (Required)
        select#user-type(v-model="showUser.idUserRole" :disabled="overlayTitle !== 'ADD NEW USER'" @blur="v$.showUser.idUserRole.$touch()")
          option(value="") Select Role
          option(
            v-for="role in communityRoles" 
            :key="role.id" 
            :value="role.id"
          ) {{ role.name }}
        .form-group__errors(v-if="v$.showUser.idUserRole.$dirty && v$.showUser.idUserRole.$errors.length")
          .form-group__error(v-if="v$.showUser.idUserRole.required.$invalid") User role is required!
      .columns
        .column.is-half
          .form-group(:class="{ 'form-group__invalid': v$.showUser.first.$errors.length }")
            label(for="first-name") First Name (Required)
            input#first-name(type="text" v-model="showUser.first" @blur="v$.showUser.first.$touch()")
            .form-group__errors(v-if="v$.showUser.first.$dirty && v$.showUser.first.$errors.length")
              .form-group__error(v-if="v$.showUser.first.required.$invalid") User first name is required!
        .column.is-half
          .form-group(:class="{ 'form-group__invalid': v$.showUser.last.$errors.length }")
            label(for="last-name") Last Name (Required)
            input#last-name(type="text" v-model="showUser.last" @blur="v$.showUser.last.$touch()")
            .form-group__errors(v-if="v$.showUser.last.$dirty && v$.showUser.last.$errors.length")
              .form-group__error(v-if="v$.showUser.last.required.$invalid") User last name is required!
      .form-group( v-if=" overlayTitle=='ADD NEW USER' " :class="{ 'form-group__invalid': v$.showUser.password.$errors.length }")
        label(for="password") Password (Required)
        input#password(
          type="password" 
          autocomplete="current-password"
          v-model="showUser.password"
          @blur="v$.showUser.password.$touch()"
        )
        .form-group__errors(v-if="v$.showUser.password.$dirty && v$.showUser.password.$errors.length")
          .form-group__error(v-if="v$.showUser.password.required.$invalid") Password is required!
      .form-group(:class="{ 'form-group__invalid': v$.showUser.email.$errors.length }")
        label(for="email") Email (Required)
        input#email(type="email" v-model="showUser.email" @blur="v$.showUser.email.$touch()")
        .form-group__errors(v-if="v$.showUser.email.$dirty && v$.showUser.email.$errors.length")
          .form-group__error(v-if="v$.showUser.email.email.$invalid") Invalid email format
          .form-group__error(v-if="v$.showUser.email.required.$invalid") User email is required!
      .form-group
        label(for="details") Details
        textarea#details(style="height:250px;" v-model="showUser.details")
       
  template(#footer)
    .flex.flex--space-between
      BaseBtn.x-lg.red-outline(
        href="#",
        @click.prevent="closeOverlay"
      ) CANCEL
      BaseBtn.x-lg.green(
        type="submit",
        form="manage-user"
        :disabled="v$.$invalid || isSubmitting"
        :class="{spinner: isSubmitting }"
      ) CONTINUE
</template>

<script>
import { defineComponent } from "vue";
import SideOverlaySlot from "@/components/overlays/SideOverlaySlot.vue";
import { userRoles } from "@/store/account";

import { register } from "@/api/accountApi";
import { apiStatus, apiStatusComputedFactory } from "@/api";
import { updateUser } from "@/api/userApi";
const { IDLE, PENDING, SUCCESS, ERROR } = apiStatus;
import { createToast } from "mosha-vue-toastify";
import useVuelidate from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";

export default defineComponent({
  setup() {
    return { v$: useVuelidate() };
  },
  components: { SideOverlaySlot },
  emits: ["closeManageUsersCommunityOverlay"],

  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    overlayTitle: {
      type: String,
      required: true,
    },
    showUser: {
      type: Object,
      required: false,
    },
  },

  computed: {
    ...apiStatusComputedFactory("addCommunityUserStatus"),
  },

  data() {
    return {
      manageCommunityStatus: IDLE,
      addCommunityUserStatus: IDLE,
      communityRoles: userRoles.communitycareorganization,
      isUpdateServerError: false,
      isSubmitting: false,
    };
  },
  validations() {
    let args = {
      showUser: {
        idUserRole: { required },
        first: { required },
        last: { required },
        password: { required },
        email: { required, email },
      },
    };
    if (this.overlayTitle != "ADD NEW USER") {
      args.showUser.password.required = false;
    }
    return args;
  },
  methods: {
    saveAndCloseOverlay() {
      if (this.overlayTitle == "ADD NEW USER") {
        this.register().then(() => {
          this.$emit("closeManageUsersCommunityOverlay");
        });
      } else {
        this.update().then(() => {
          if (!this.isUpdateServerError) {
            this.$emit("closeManageUsersCommunityOverlay");
          } else {
            this.isUpdateServerError = false;
          }
        });
      }
    },
    closeOverlay() {
      this.$emit("closeManageUsersCommunityOverlay");
    },

    async register() {
      this.isSubmitting = true;
      this.addCommunityUserStatus = PENDING;
      try {
        await register({
          user: {
            createdAt: null,
            details: this.showUser.details,
            email: this.showUser.email,
            first: this.showUser.first,
            idUserRole: parseInt(this.showUser.idUserRole),
            last: this.showUser.last,
            password: this.showUser.password,
            signInCount: 0,
            status: null,
            updatedAt: null,
          },
          idCommunity: this.$route.params.idCommunity,
        });

        this.addCommunityUserStatus = SUCCESS;
      } catch (err) {
        this.addCommunityUserStatus = ERROR;
        createToast(err?.response?.data?.message || "There was a problem", {
          timeout: 4000,
          type: "danger",
          position: "bottom-right",
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    async update() {
      this.isSubmitting = true;
      this.manageCommunityStatus = PENDING;
      try {
        await updateUser(this.showUser.id, {
          user: {
            details: this.showUser.details,
            email: this.showUser.email,
            first: this.showUser.first,
            last: this.showUser.last,
          },
        });

        this.manageCommunityStatus = SUCCESS;
      } catch (err) {
        this.manageCommunityStatus = ERROR;
        createToast(err?.response?.data?.message || "There was a problem", {
          timeout: 4000,
          type: "danger",
          position: "bottom-right",
        });
        this.isUpdateServerError = true;
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
.patient-section {
  margin-bottom: 0;
  padding: 37px 24px 25px;
  background-color: $primaryBgBlue;
  p {
    margin: 0 0 12px;
    font-size: 21px;
    font-weight: 500;
    line-height: 1.1;
    color: $primaryLightBlue;
    overflow: hidden;
    strong {
      font-weight: 700;
    }
  }
}
form#manage-user {
  margin: 12px 0 20px;
  .columns {
    margin-bottom: 0px;
    .column {
      padding-bottom: 0px;
    }
  }
}
.overlay-show-success {
  p {
    margin: 41px 0 60px;
  }
}
</style>
